import { useNavigate } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import PackageCard, { type Package } from "./components/PackageCard";
import { useAdmin } from "../../contexts/AdminContext";

const DEFAULT_PACKAGES: Package[] = [
  { id: "pack1", tickets: 1, bonus: 0, price: 5, pricePerTicket: 5.0, secondaryLightColor: "text-slate-600", popular: false },
  { id: "pack5", tickets: 5, bonus: 0, price: 22, pricePerTicket: 4.4, secondaryLightColor: "text-sky-600", popular: false },
  { id: "pack10", tickets: 10, bonus: 0, price: 40, pricePerTicket: 4.0, secondaryLightColor: "text-secondary-dark", popular: false },
  { id: "pack25", tickets: 25, bonus: 2, price: 90, pricePerTicket: 3.33, badge: "⭐ BELIEBT", badgeColor: "bg-primary", secondaryLightColor: "text-primary", popular: true },
  { id: "pack50", tickets: 50, bonus: 8, price: 150, pricePerTicket: 2.63, badge: "💎 WERT", badgeColor: "bg-secondary", secondaryLightColor: "text-secondary-dark", popular: false },
  { id: "pack100", tickets: 100, bonus: 20, price: 250, pricePerTicket: 2.08, badge: "👑 BEST", badgeColor: "bg-slate-800", secondaryLightColor: "text-slate-800", popular: false },
];

const guarantees = [
  { icon: "ri-lock-line", label: "Sichere SSL-Zahlung", color: "text-secondary", bg: "bg-emerald-50" },
  { icon: "ri-award-line", label: "Notariell beglaubigte Ziehung", color: "text-sky-500", bg: "bg-sky-50" },
  { icon: "ri-refund-2-line", label: "Rückerstattung bei Stornierung", color: "text-primary", bg: "bg-amber-50" },
  { icon: "ri-customer-service-2-line", label: "Kundensupport 7 Tage/Woche", color: "text-primary", bg: "bg-orange-50" },
];

export default function Forfaits() {
  const navigate = useNavigate();
  const { settings } = useAdmin();

  const packages: Package[] = settings.packages.length > 0
    ? settings.packages.map((p, idx) => ({
        id: `pack-${idx}`,
        tickets: p.count,
        bonus: 0,
        price: parseFloat(p.price.replace("€", "").trim()) || 0,
        pricePerTicket: (parseFloat(p.price.replace("€", "").trim()) || 0) / p.count,
        secondaryLightColor: p.popular ? "text-primary" : "text-slate-600",
        popular: p.popular || false,
        badge: p.popular ? "⭐ BELIEBT" : undefined,
        badgeColor: p.popular ? "bg-primary" : undefined,
      }))
    : DEFAULT_PACKAGES;

  const handleSelect = (pkg: Package) => {
    navigate(`/paiement?package=${pkg.id}`);
  };

  return (
    <main className="min-h-screen font-inter bg-slate-50">
      <Navbar />

      {/* Page Header */}
      <div className="bg-gradient-to-b from-slate-100 to-slate-50 pt-32 pb-16 text-center px-6">
        <nav className="text-sm text-slate-400 mb-6" aria-label="Breadcrumb">
          <a href="/" className="hover:text-slate-600 cursor-pointer">Startseite</a>
          <span className="mx-2">/</span>
          <span className="text-slate-700 font-medium">Pakete</span>
        </nav>
        <h1 className="font-playfair text-4xl md:text-6xl font-bold text-slate-900 mb-4">
          Wählen Sie Ihr Paket
        </h1>
        <p className="text-slate-500 text-lg max-w-xl mx-auto leading-relaxed">
          Je mehr Lose Sie haben, desto höher sind Ihre Chancen, die Traumvilla zu gewinnen!
        </p>
        <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-5 py-2 rounded-full mt-6 text-sm font-semibold">
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-time-line"></i></span>
          Ziehung am {settings.nextDrawDate} — Bereiten Sie sich vor!
        </div>
      </div>

      {/* Packages Grid — single row */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 items-start">
          {packages.map((pkg) => (
            <PackageCard key={pkg.id} pkg={pkg} onSelect={handleSelect} />
          ))}
        </div>
      </section>

      {/* Guarantee Strip */}
      <section className="bg-white border-t border-b border-slate-100 py-10">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {guarantees.map((g, i) => (
              <div key={i} className="flex flex-col items-center text-center gap-3">
                <div className={`w-12 h-12 ${g.bg} ${g.color} rounded-full flex items-center justify-center`}>
                  <i className={`${g.icon} text-xl`}></i>
                </div>
                <span className="text-slate-700 text-sm font-medium">{g.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Info Banner */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <div className="bg-slate-900 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-white font-bold text-xl mb-1">Hilfe bei der Auswahl benötigt?</h3>
            <p className="text-slate-400 text-sm">Unser Team steht Ihnen 7 Tage die Woche zur Beratung zur Verfügung.</p>
          </div>
          <a
            href="mailto:info@germantraumhauslotterie.de"
            className="bg-white text-slate-900 font-semibold px-8 py-3 rounded-full hover:bg-slate-100 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-mail-line"></i></span>
            Kontaktieren Sie uns
          </a>
        </div>
      </section>

      <Footer />
    </main>
  );
}
